import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { BaseStore, Product, WindowRef } from '@spartacus/core';
import { CurrentProductService, JSONLD_PRODUCT_BUILDER, JsonLdBuilder } from '@spartacus/storefront';
import { DateTime } from 'luxon';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { EnvironmentService } from '../../features/services/environment.service';

interface WarehouseWsDTO {
  code: string;
  name: string;
}

declare module '@spartacus/core' {
  export interface BaseStore {
    warehouses: WarehouseWsDTO[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class GGProductSchemaBuilder {
  private baseStore?: BaseStore;
  private DATE_FORMAT: string = 'yyyy-MM-DD';

  constructor(
    private router: Router,
    private currentProduct: CurrentProductService,
    private environmentService: EnvironmentService,
    private winRef: WindowRef,
    @Optional()
    @Inject(JSONLD_PRODUCT_BUILDER)
    protected builders: JsonLdBuilder<Product>[]
  ) {}

  build(active: string): Observable<any> {
    return this.currentProduct.getProduct().pipe(
      filter((product: any) => !!product),
      switchMap((product: Product) => {
        if (product) {
          return combineLatest(this.collect(product, active)).pipe(
            map((res: {}[]) => {
              return Object.assign({}, ...res);
            })
          );
        }
        return of({});
      })
    );
  }

  protected collect(product: Product, active: string): Observable<any>[] {
    if (!product || !product.code) {
      return [];
    }

    const schemaToBuild = this.prepareGGSchema(product, active);

    return [
      of({
        '@context': 'http://schema.org',
        '@type': 'Product',
        ...schemaToBuild,
      }),
    ];
  }

  private prepareGGSchema(product: Product, active: string): any {
    const offers = {
      ...this.setOffers('Hisense', product),
    };

    const url = this.getCurrentLocationUrl();
    const image = this.getProductImage(product);
    const rating = this.getAggregateRating(product);
    const reviews = this.getReviews(product);

    const schemaToBuild = {
      '@id': url,
      name: product.name ? product.name : '',
      image: image,
      brand: this.getBrand('Hisense'),
      ...(this.environmentService.getCurrentEnvironment().eShop.showPrice[active]
        ? { ...(product.price ? { offers: offers } : {}) }
        : {}),
      model: product.modelMark ? product.modelMark : '',
      gtin13: product.ean ? product.ean : '',
      mpn: product.ean ? product.ean : '',
      description: product.technicalDesc ? product.technicalDesc : '',
      ...(this.environmentService.getCurrentEnvironment().reviews.show[active]
        ? { ...(product.reviews && product.reviews.length > 0 ? { review: reviews } : null) }
        : {}),
      ...(this.environmentService.getCurrentEnvironment().reviews.show[active]
        ? { ...(product.reviews?.length! > 0 ? { aggregateRating: rating } : {}) }
        : {}),
      sku: parseInt(product.code!),
    };

    return schemaToBuild;
  }

  protected getCurrentLocationUrl(): string | undefined {
    if (this.winRef.isBrowser()) {
      return this.router['location']._locationStrategy._platformLocation._location.href;
    } else {
      return undefined;
    }
  }

  private removeProperty(schema: object, prop: string) {
    delete schema[prop];
  }

  protected getProductImage(product: Product): { image?: string } {
    const image = product.images?.PRIMARY?.['zoom']?.url;
    const alt = product.images?.GALLERY?.[0]?.product.url;
    return image ? { image } : alt;
  }

  protected getAggregateRating(product: Product) {
    let rating = {};
    if (product.reviews) {
      rating = {
        ...rating,
        '@type': 'AggregateRating',
        ratingValue: product.averageRating ? product.averageRating : '',
        reviewCount: product.reviews?.length,
        bestRating: 5,
        worstRating: 1,
      };
    }
    return rating;
  }

  private getDate(number, format) {
    return DateTime.fromISO(number).toFormat(format);
  }

  protected getReviews(product: Product) {
    let review = {};
    const reviews: {}[] = [];
    const rating = this.getBaseRating();
    if (product.reviews) {
      for (let i = 0; i < product.reviews?.length; i++) {
        const date = new Date(product.reviews[i].date!).toISOString();
        const reviewRating = this.getReviewRating(product, rating, i);
        review = {
          ...review,
          '@type': 'Review',
          datePublished: this.getDate(date, this.DATE_FORMAT),
          reviewBody: product.reviews[i].comment ? product.reviews[i].comment : '',
          reviewRating: reviewRating,
        };
        if (product.reviews[i].reviewAsGuest) {
          review = {
            ...review,
            author: {
              '@type': 'Person',
              name: product.reviews[i].alias ? product.reviews[i].alias : '',
            },
          };
        } else {
          review = {
            ...review,
            author: {
              '@type': 'Person',
              name: product.reviews[i].principal ? product.reviews[i].principal?.name : '',
            },
          };
        }
        reviews.push(review);
      }
    }
    return reviews;
  }

  protected getBrand(brand: string) {
    return {
      '@type': 'Brand',
      name: 'Hisense',
    };
  }

  protected getBaseRating() {
    return {
      '@type': 'Rating',
      bestRating: 5,
      worstRating: 1,
    };
  }
  protected getReviewRating(product: Product, rating, index) {
    if (product.reviews) {
      rating = {
        ...rating,
        ratingValue: product.reviews[index].rating,
      };
    }
    return rating;
  }

  private setOffers(brandName: string, product: Product) {
    const offers = {
      '@type': 'Offer',
      ...(product.price?.value ? { price: product.price?.value } : ''),
      ...(product.price?.currencyIso ? { priceCurrency: product.price?.currencyIso } : ''),
      availability:
        product.stock && product.stock.stockLevel! > 0 ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
      url: this.getCurrentLocationUrl(),
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        brand: {
          '@type': 'Brand',
          name: brandName,
        },
      },
    };

    if (this.baseStore?.warehouses == undefined || this.baseStore?.warehouses.length == 0) {
      this.removeProperty(offers, 'availability');
    }

    return offers;
  }

  setBaseStore(baseStore: BaseStore) {
    this.baseStore = baseStore;
  }
}
